"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { cn } from "@repo/design-system/utils/cn";
import { type ReactNode, useEffect, useState } from "react";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobadComponentProps } from "~/types/job";
import styles from "./application-process-eon-uk.module.css";
const STEP_CONFIGS = {
  default: [{
    icon: "notification.svg",
    text: "Online Application"
  }, {
    icon: "messenger.svg",
    text: "Interview"
  }, {
    icon: "user_group_alt.svg",
    text: "Contract and Onboarding"
  }],
  entryLevel: [{
    icon: "notification.svg",
    text: "Online Application"
  }, {
    icon: "checkbox_checked.svg",
    text: "Online Assessment"
  }, {
    icon: "talk_to_us.svg",
    text: "Introductory telephone interview"
  }, {
    icon: "messenger.svg",
    text: "Assessment centre"
  }, {
    icon: "handshake.svg",
    text: "Job Offer"
  }],
  executive: [{
    icon: "notification.svg",
    text: "Online Application"
  }, {
    icon: "checkbox_checked.svg",
    text: "CV Review"
  }, {
    icon: "talk_to_us.svg",
    text: "Functional Interview"
  }, {
    icon: "messenger.svg",
    text: "Leadership Presentation"
  }, {
    icon: "handshake.svg",
    text: "Job Offer"
  }]
} as const;

// Step gap: mobile 96px, desktop 125px
const CONTAINER_HEIGHT: Record<number, string> = {
  3: "h-48 md:h-[250px]",
  5: "h-96 md:h-[500px]"
};
function Step(t0) {
  const $ = _c(20);
  const {
    children,
    isEven,
    index,
    length,
    bubbleStyle,
    boxStyle
  } = t0;
  const t1 = isEven ? "-translate-x-4 md:right-1/2 md:translate-x-4 md:flex-row-reverse md:text-end lg:translate-x-6" : "-translate-x-4 md:left-1/2 lg:-translate-x-6";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("absolute flex -translate-y-1/2 gap-4 **:transition-[background-color,color,scale,translate] **:duration-300 **:ease-in-out", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${index * (100 / (length - 1))}%`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      top: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== bubbleStyle) {
    t5 = cn("relative my-auto flex aspect-square h-8 w-8 items-center justify-center rounded-full lg:h-12 lg:w-12", bubbleStyle);
    $[4] = bubbleStyle;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = index + 1;
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = isEven && "md:py-4";
  let t9;
  if ($[9] !== boxStyle || $[10] !== t8) {
    t9 = cn("bg-gray-200 p-4 py-2 lg:p-6", t8, boxStyle);
    $[9] = boxStyle;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== children || $[13] !== t9) {
    t10 = <div className={t9}>{children}</div>;
    $[12] = children;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t2 || $[17] !== t4 || $[18] !== t7) {
    t11 = <div className={t2} style={t4}>{t7}{t10}</div>;
    $[15] = t10;
    $[16] = t2;
    $[17] = t4;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}
export default function ApplicationProcessEonUk(t0) {
  const $ = _c(23);
  const {
    job
  } = t0;
  const t = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  let configKey = "default";
  if (job.data.entryLevel === "Entry Level") {
    configKey = "entryLevel";
  } else {
    if (job.data.entryLevel === "Executive Management") {
      configKey = "executive";
    }
  }
  const steps = STEP_CONFIGS[configKey];
  let t1;
  let t2;
  if ($[0] !== steps.length) {
    t1 = () => {
      let interval = null;
      const start = () => {
        interval = setInterval(() => {
          setActiveIndex(prev => (prev + 1) % steps.length);
        }, 2000);
      };
      const stop = () => {
        if (interval !== null) {
          clearInterval(interval);
          interval = null;
        }
      };
      const handleVisibilityChange = () => {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      };
      start();
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        stop();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t2 = [steps.length];
    $[0] = steps.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const containerHeight = CONTAINER_HEIGHT[steps.length] ?? "h-96 md:h-[500px]";
  let t3;
  if ($[3] !== t.jobad_process_hl) {
    t3 = <h2 className="mb-16 font-bold text-3xl text-foreground md:text-4xl lg:mb-24 lg:text-5xl">{t.jobad_process_hl}</h2>;
    $[3] = t.jobad_process_hl;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute top-0 bottom-0 -z-10 w-1 -translate-x-1/2 bg-secondary md:left-1/2" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== containerHeight) {
    t5 = cn("relative w-full", containerHeight);
    $[6] = containerHeight;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== activeIndex || $[9] !== steps) {
    let t7;
    if ($[11] !== activeIndex || $[12] !== steps.length) {
      t7 = (step, index) => <Step boxStyle={cn("md:rounded-md", {
        "bg-secondary": index !== activeIndex,
        "bg-primary text-headline_font_color": index === activeIndex && index !== steps.length - 1,
        "bg-[#18A087] text-headline_font_color": index === activeIndex && index === steps.length - 1
      })} bubbleStyle={cn({
        "bg-secondary": index !== activeIndex,
        "scale-110 bg-primary text-headline_font_color": index === activeIndex && index !== steps.length - 1,
        "bg-[#18A087] text-headline_font_color": index === activeIndex && index === steps.length - 1,
        [styles.bubble]: index === activeIndex && index !== steps.length - 1
      })} index={index} isEven={index % 2 === 0} key={index} length={steps.length}><div className="flex h-[72px] w-[250px] items-center justify-start gap-x-4 md:w-[410px] md:justify-center"><CustomImage alt="" className="shrink-0" height={32} src={`images/icons/hungary/${step.icon}`} width={32} />{step.text}</div></Step>;
      $[11] = activeIndex;
      $[12] = steps.length;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = steps.map(t7);
    $[8] = activeIndex;
    $[9] = steps;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="relative my-20 ml-4 w-full md:ml-0">{t4}<div className={t5}>{t6}</div></div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== job.data.entryLevel) {
    t8 = job.data.entryLevel === "Entry Level" && <div className="flex justify-center pt-10"><CustomLink blank={true} className="flex items-center rounded bg-primary px-6 py-3 text-white text-xl hover:border-2 hover:border-primary hover:bg-white hover:text-primary" href="https://www.eon-uk-careers.com/en/early-careers/degree-apprenticeships.html"><span>Degree Apprenticeships</span></CustomLink></div>;
    $[17] = job.data.entryLevel;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t3 || $[20] !== t7 || $[21] !== t8) {
    t9 = <section className="w-full overflow-hidden px-6 py-10 text-lg text-white md:px-10 md:py-20 md:text-2xl lg:text-3xl print:hidden" id="applicationProcess"><div className="mx-auto max-w-7xl">{t3}{t7}{t8}</div></section>;
    $[19] = t3;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}